import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import { useEffect } from 'react';
import rtlPlugin from 'stylis-plugin-rtl';

import type { ThemeDirection } from '../types';

type RtlProps = {
    children: React.ReactNode;
    direction: ThemeDirection;
};

const cacheRtl = createCache({
    key: 'rtl',
    prepend: true,
    stylisPlugins: [rtlPlugin],
});

/**
 * Component to handle right-to-left (RTL) direction in the application.
 * When direction is set to 'rtl', it wraps children with a CacheProvider to enable RTL styling.
 * This component also sets the document's direction attribute accordingly.
 *
 * @param props - Component props
 * @param props.children - The child elements to be rendered within the component
 * @param props.direction - The text direction to apply ('ltr' or 'rtl')
 * @returns A React component that handles RTL direction
 */
export function Rtl({ children, direction }: RtlProps) {
    useEffect(() => {
        document.dir = direction;
    }, [direction]);

    if (direction === 'rtl') {
        return <CacheProvider value={cacheRtl}>{children}</CacheProvider>;
    }

    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{children}</>;
}
